/*
 * Product page behaviour for the 2026-09-24 SKU cleanup (sections/hsf-pdp.liquid).
 *
 * 1. Section nav opens the target accordion before scrolling to it.
 * 2. The mobile sticky bar is a proxy for the main product form: it shows the
 *    selected pack and purchase mode and clicks the real Add to cart button, so
 *    the variant, selling plan and quantity always come from one form.
 * 3. ?purchase=subscription|one-time preselects the purchase mode (landing links).
 * 4. A small set of hsf_* events, sent only to our GA4 property. The old PDP
 *    scripts sent them without send_to, which leaked them into Google Ads and a
 *    second GA4 property (baseline/tracking_audit.md).
 * 5. The ingredient explorer (snippets/hsf-ingredient-explorer.liquid): tiles open one dialog.
 */
(() => {
  const root = document.querySelector('[data-hsf-pdp]');
  if (!root || window.HermeticaPdp20260924) return;
  window.HermeticaPdp20260924 = true;

  const GA4_ID = 'G-R5GK4CN152';
  const handle = root.dataset.productHandle || '';
  const productId = root.dataset.productId || '';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const moneyFormat = (cents) => `$${(cents / 100).toFixed(2)}`;

  const checkedPack = () => document.querySelector('.mrk-bundle__card-radio:checked');
  const checkedPlan = () => document.querySelector('.mrk-bundle__sub-radio:checked:not(:disabled)');

  const state = () => {
    const pack = checkedPack();
    const plan = checkedPlan();
    return {
      product_id: productId,
      product_handle: handle,
      variant_id: pack?.dataset.variantId || '',
      pack: pack?.value || '',
      purchase_type: plan?.dataset.isSubscription === 'true' ? 'subscription' : 'one_time',
      currency: 'USD',
    };
  };

  const emit = (name, params = {}) => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', `hsf_${name}`, { ...state(), ...params, send_to: GA4_ID });
  };

  /* 1. Section nav */
  root.querySelectorAll('.hsf-pdp__nav-link').forEach((link) => {
    link.addEventListener('click', (event) => {
      const id = (link.getAttribute('href') || '').slice(1);
      const target = id && document.getElementById(id);
      if (!target) return;
      event.preventDefault();
      if (target.tagName === 'DETAILS') target.open = true;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      history.replaceState(null, '', `#${id}`);
      emit('section_nav_click', { section: id });
    });
  });

  if (location.hash) {
    const target = document.getElementById(location.hash.slice(1));
    if (target && target.tagName === 'DETAILS' && root.contains(target)) target.open = true;
  }

  /* 2. Mobile sticky bar as a proxy for the main form */
  const bar = document.querySelector('[data-hsf-mobile-buybar]');
  const mainAddButton = () =>
    Array.from(document.querySelectorAll('#MainContent button[name="add"][type="submit"]')).find(
      (button) => !button.closest('[data-hsf-mobile-buybar], sticky-add-to-cart')
    );

  const planPrice = (price, plan) => {
    if (!plan || plan.dataset.isSubscription !== 'true') return price;
    const allocatedPrice = Number(plan.dataset.planPrice);
    if (plan.dataset.planPrice !== undefined && plan.dataset.planPrice !== '' && Number.isSafeInteger(allocatedPrice) && allocatedPrice >= 0) return allocatedPrice;
    const value = parseFloat(plan.dataset.adjustmentValue) || 0;
    switch (plan.dataset.adjustmentType) {
      case 'percentage':
        return Math.round(price - (price * value) / 100);
      case 'fixed_amount':
        return price - value;
      case 'price':
        return value;
      default:
        return price;
    }
  };

  const syncBar = () => {
    const pack = checkedPack();
    const plan = checkedPlan();
    const label = bar?.querySelector('[data-hsf-buybar-label]');
    const price = bar?.querySelector('[data-hsf-buybar-price]');
    const button = bar?.querySelector('[data-hsf-buybar-add]');
    if (!pack) return;
    const subscribe = plan?.dataset.isSubscription === 'true';
    const cents = planPrice(parseInt(pack.dataset.price, 10) || 0, plan);
    if (label) label.textContent = `${bar.dataset.shortName || ''} · ${pack.value}`;
    if (price) price.textContent = subscribe ? `${moneyFormat(cents)} monthly` : moneyFormat(cents);
    const soldOut = pack.getAttribute('aria-disabled') === 'true';
    if (button) {
      const beforePurchase = bar.dataset.beforePurchase === 'true';
      button.textContent = beforePurchase ? 'Choose options' : soldOut ? 'Sold out' : subscribe ? 'Subscribe' : 'Add to cart';
      if (soldOut && !beforePurchase) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }
    // Horizon's desktop sticky bar only knows the variant; mirror the purchase mode and price.
    const sticky = document.querySelector('sticky-add-to-cart');
    const stickyLabel = sticky?.querySelector('.add-to-cart-text__content > span:first-child');
    const stickyPrice = sticky?.querySelector('.add-to-cart-text__content .price');
    if (stickyLabel && !soldOut) stickyLabel.textContent = subscribe ? 'Subscribe' : 'Add to cart';
    if (stickyPrice) stickyPrice.textContent = subscribe ? `${moneyFormat(cents)}/mo` : moneyFormat(cents);
  };

  const syncBars = () => {
    syncBar();
    window.setTimeout(syncBar, 200);
  };

  let proxying = false;
  if (bar) {
    bar.addEventListener('click', (event) => {
      const button = event.target.closest('[data-hsf-buybar-add]');
      if (!button) return;
      // Navigation must remain available even when the selected pack cannot be added.
      if (bar.dataset.beforePurchase === 'true') {
        document.getElementById('product-buy')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        emit('buybox_open', { placement: 'mobile_sticky' });
        return;
      }
      const add = mainAddButton();
      if (!add) {
        document.getElementById('product-buy')?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        return;
      }
      if (button.hasAttribute('aria-disabled') || add.disabled) return;
      emit('add_to_cart_intent', { placement: 'mobile_sticky' });
      proxying = true;
      add.click();
      proxying = false;
    });
  }
  syncBars();

  // Shopify owns the selected variant and form ID. Bulk deep links retain their pack.
  const revealSelectedBulk = () => {
    const bulk = checkedPack()?.closest('[data-hsf-bulk-option]');
    if (bulk) bulk.open = true;
  };
  revealSelectedBulk();

  /* 3. ?purchase= preselects the purchase mode */
  const requested = new URLSearchParams(location.search).get('purchase');
  if (requested === 'subscription' || requested === 'one-time') {
    const radio = document.querySelector(
      `.mrk-bundle__sub-radio[data-is-subscription="${requested === 'subscription'}"]:not(:disabled)`
    );
    if (radio && !radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* 4. Events. Horizon re-renders the buy box on variant change, so listen on document. */
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.matches('.mrk-bundle__card-radio')) emit('bundle_select', { selected_label: target.value });
    if (target.matches('.mrk-bundle__sub-radio')) emit('purchase_option_select');
    if (target.matches('.mrk-bundle__card-radio, .mrk-bundle__sub-radio')) syncBars();
  });
  document.addEventListener('hsf:purchase-visibility', syncBar);
  document.addEventListener('variant:update', () => window.requestAnimationFrame(() => {
    revealSelectedBulk();
    syncBars();
  }));

  document.addEventListener('click', (event) => {
    const add = event.target.closest('#MainContent button[name="add"][type="submit"]');
    if (add && !proxying && !add.closest('[data-hsf-mobile-buybar]')) emit('add_to_cart_intent', { placement: 'main' });
    // 2026-09-26 additions from sections/product-information.liquid
    const pairAdd = event.target.closest('[data-hsf-pair] [data-hsf-add-variant]');
    if (pairAdd) emit('pair_add_click', { pair_variant_id: pairAdd.dataset.hsfAddVariant || '' });
    if (event.target.closest('.hsf-rating-link')) emit('rating_link_click');
  });

  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (details instanceof HTMLDetailsElement && details.open && details.matches('.mrk-bundle__sub-details')) {
        emit('subscription_details_open');
      }
    },
    true
  );

  root.querySelectorAll('.hsf-pdp__faq').forEach((faq) => {
    faq.addEventListener('toggle', () => {
      if (faq.open) emit('faq_open', { faq_index: faq.dataset.hsfPdpFaq || '' });
    });
  });

  /* 5. Ingredient explorer: every tile opens one shared dialog; arrows step through the formula. */
  const explorer = root.querySelector('[data-hsf-explorer]');
  const sheet = explorer?.querySelector('[data-hsf-explorer-dialog]');
  if (explorer && sheet && typeof sheet.showModal === 'function') {
    const tiles = Array.from(explorer.querySelectorAll('[data-hsf-explorer-open]'));
    const body = sheet.querySelector('[data-hsf-explorer-body]');
    const more = explorer.querySelector('[data-hsf-explorer-more]');
    let current = -1;

    const show = (index, direction = 0) => {
      const next = (index + tiles.length) % tiles.length;
      const detail = tiles[next]?.parentElement.querySelector('template');
      if (!detail) return;
      current = next;
      body.replaceChildren(detail.content.cloneNode(true));
      body.scrollTop = 0;
      if (sheet.open) {
        sheet.style.setProperty('--hsf-dir', String(direction || 1));
        sheet.removeAttribute('data-swap');
        void sheet.offsetWidth;
        sheet.setAttribute('data-swap', '');
      } else {
        // Grow from the tile that was pressed.
        const box = tiles[next].getBoundingClientRect();
        sheet.style.setProperty('--hsf-ox', `${Math.round(((box.left + box.width / 2) / window.innerWidth) * 100)}%`);
        sheet.style.setProperty('--hsf-oy', `${Math.round(((box.top + box.height / 2) / window.innerHeight) * 100)}%`);
        sheet.removeAttribute('data-swap');
        sheet.showModal();
      }
      sheet.querySelector('.hsf-explorer__title')?.focus({ preventScroll: true });
      emit('ingredient_open', { ingredient: tiles[next].dataset.hsfIngredient || '', ingredient_index: next + 1 });
    };

    explorer.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-hsf-explorer-open]');
      if (tile) return show(tiles.indexOf(tile));
      const step = event.target.closest('[data-hsf-explorer-step]');
      if (step) return show(current + Number(step.dataset.hsfExplorerStep), Number(step.dataset.hsfExplorerStep));
      // A press on the dimmed area lands on the dialog element itself.
      if (event.target.closest('[data-hsf-explorer-close]') || event.target === sheet) return sheet.close();
      if (more && event.target.closest('[data-hsf-explorer-more]')) {
        const opening = explorer.hasAttribute('data-collapsed');
        const firstHidden = opening ? tiles.find((item) => item.offsetParent === null) : null;
        explorer.toggleAttribute('data-collapsed', !opening);
        more.setAttribute('aria-expanded', String(opening));
        more.querySelector('[data-hsf-explorer-more-label]').textContent = opening ? 'Show fewer ingredients' : `Show all ${tiles.length} ingredients`;
        if (firstHidden) firstHidden.focus({ preventScroll: true });
        else if (!opening) explorer.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' });
        emit('ingredient_list_toggle', { expanded: opening });
      }
    });
    sheet.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') show(current + 1, 1);
      if (event.key === 'ArrowLeft') show(current - 1, -1);
    });
    sheet.addEventListener('close', () => {
      const tile = tiles[current];
      // The last one viewed may sit in the folded part of the grid; open the grid so focus has somewhere to land.
      if (tile && tile.offsetParent === null && more) more.click();
      tile?.focus({ preventScroll: true });
    });
    if (more) {
      more.hidden = false;
      explorer.setAttribute('data-collapsed', '');
    }
  }

  if ('IntersectionObserver' in window) {
    const seen = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const part = entry.target.dataset.hsfPdpPart;
          if (!entry.isIntersecting || seen.has(part)) return;
          seen.add(part);
          emit('section_view', { section: part });
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.35 }
    );
    root.querySelectorAll('[data-hsf-pdp-part]').forEach((node) => observer.observe(node));
  }
})();
